import { QuestionOptions, EffectiveChoiceItem } from '../types/survey.types';

export const NULL_OPTION_VALUE = '__NULL_OPTION__';
export const DONT_KNOW_OPTION_VALUE = '__DONT_KNOW_OPTION__';

export const NULL_OPTION_LABEL = 'Nulo';
export const DONT_KNOW_OPTION_LABEL = 'Não sei responder';

export const normalizeQuestionOptions = (options?: QuestionOptions): QuestionOptions => {
  const normalized = options ?? {};
  return {
    ...normalized,
    choices: Array.isArray(normalized.choices) ? normalized.choices : [],
    randomizeChoices: Boolean(normalized.randomizeChoices),
    includeNullOption: Boolean(normalized.includeNullOption),
    includeDontKnowOption: Boolean(normalized.includeDontKnowOption),
    maxSelections: typeof normalized.maxSelections === 'number' ? normalized.maxSelections : 0,
    enforceSelectionLimit: Boolean(normalized.enforceSelectionLimit),
  };
};

export const buildEffectiveChoices = (options?: QuestionOptions): EffectiveChoiceItem[] => {
  const normalized = normalizeQuestionOptions(options);
  const baseChoices = normalized.choices || [];
  const effectiveChoices: EffectiveChoiceItem[] = baseChoices.map((choice) => ({
    value: String(choice.value),
    label: String(choice.label),
    ...(choice.filter ? { filter: choice.filter } : {}),
    kind: 'base',
  }));

  if (normalized.includeNullOption) {
    effectiveChoices.push({
      value: NULL_OPTION_VALUE,
      label: NULL_OPTION_LABEL,
      kind: 'null',
    });
  }

  if (normalized.includeDontKnowOption) {
    effectiveChoices.push({
      value: DONT_KNOW_OPTION_VALUE,
      label: DONT_KNOW_OPTION_LABEL,
      kind: 'dont_know',
    });
  }

  return effectiveChoices;
};

const COMBINING_DIACRITIC_MARK_START = String.fromCharCode(0x0300);
const COMBINING_DIACRITIC_MARK_END = String.fromCharCode(0x036f);
const COMBINING_DIACRITIC_MARKS_PATTERN = new RegExp(
  `[${COMBINING_DIACRITIC_MARK_START}-${COMBINING_DIACRITIC_MARK_END}]`,
  'g'
);

const normalizeForSearch = (value: string): string =>
  value
    .normalize('NFD')
    .replace(COMBINING_DIACRITIC_MARKS_PATTERN, '')
    .toLowerCase()
    .trim();

/**
 * Spec 033: aplica os filtros de resposta (botões + texto digitado) sobre a lista de
 * opções efetivas. Botões combinam por "ou" entre si; o texto digitado combina por "e"
 * com o(s) botão(ões) selecionado(s).
 *
 * Adendo: as opções especiais "Nulo" e "Não sei responder" (kind !== 'base') nunca são
 * escondidas por nenhum filtro — permanecem sempre visíveis quando a pergunta as inclui.
 */
export const filterChoices = (
  choices: EffectiveChoiceItem[],
  selectedFilters: string[],
  filterText: string
): EffectiveChoiceItem[] => {
  const normalizedText = normalizeForSearch(filterText || '');

  return choices.filter((choice) => {
    if (choice.kind && choice.kind !== 'base') return true;

    const matchesButtonFilter =
      selectedFilters.length === 0 || (choice.filter != null && selectedFilters.includes(choice.filter));
    const matchesTypedFilter =
      normalizedText.length === 0 || normalizeForSearch(choice.label).includes(normalizedText);

    return matchesButtonFilter && matchesTypedFilter;
  });
};

/**
 * Spec 033: valores de filtro distintos presentes nas opções, em ordem alfabética
 * (locale pt-BR, sem diferenciar maiúsculas/acentos), para montar os botões de filtro
 * sem duplicidade.
 */
export const getDistinctFilterValues = (choices: EffectiveChoiceItem[]): string[] => {
  const seen = new Set<string>();
  const distinct: string[] = [];
  for (const choice of choices) {
    if (choice.filter && !seen.has(choice.filter)) {
      seen.add(choice.filter);
      distinct.push(choice.filter);
    }
  }
  return distinct.sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));
};

/**
 * Adendo: quando "Exigir filtro antes de exibir opções" mantém a lista escondida, as
 * opções especiais "Nulo"/"Não sei responder" continuam visíveis — só as opções
 * regulares ficam ocultas.
 */
export const keepOnlySpecialChoices = (choices: EffectiveChoiceItem[]): EffectiveChoiceItem[] =>
  choices.filter((choice) => choice.kind !== 'base');

/** Quantidade de opções regulares (exclui "Nulo"/"Não sei responder") — usada na mensagem de contagem. */
export const countRegularChoices = (choices: EffectiveChoiceItem[]): number =>
  choices.filter((choice) => choice.kind === 'base').length;

/**
 * Spec 033 (adendo): decide se as opções de uma pergunta devem ficar escondidas até o
 * respondente aplicar um filtro — útil para listas com centenas de opções (ex.:
 * candidatos a deputado estadual/federal). Nunca esconde as opções sem saída: só entra
 * em vigor quando a pergunta realmente tem como ser filtrada (valores de filtro nas
 * opções ou filtro digitado habilitado).
 */
export const shouldHideOptionsUntilFiltered = (
  requiresFilterToShowOptions: boolean | undefined,
  choices: EffectiveChoiceItem[],
  allowsTypedFilter: boolean | undefined,
  selectedFilters: string[],
  filterText: string
): boolean => {
  if (!requiresFilterToShowOptions) return false;

  const hasFilterCapability = getDistinctFilterValues(choices).length > 0 || Boolean(allowsTypedFilter);
  if (!hasFilterCapability) return false;

  const hasActiveFilter = selectedFilters.length > 0 || filterText.trim().length > 0;
  return !hasActiveFilter;
};
